/**
 * Расчёт результата розыгрыша (раздел 9–10 ТЗ). Чистая детерминированная функция.
 *
 * Порядок:
 *  1. Вклад игрока c_p = взнос + сумма ставок по кругам (за вычетом возвращённых неуравнённых).
 *  2. Банки строятся «слоями» по уровням вкладов. Претенденты слоя — не спасовавшие игроки,
 *     чей вклад покрывает уровень. Слой без претендентов («мёртвые» деньги спасовавших)
 *     присоединяется к предыдущему банку; соседние слои с одинаковыми претендентами сливаются.
 *  3. Комиссия: по каждому кругу floor(новые_взносы × ставка), не выше открытого предела круга.
 *     Сумма комиссии распределяется по банкам пропорционально их размеру (метод наибольших остатков).
 *  4. Особые случаи → отмена с полным возвратом и нулевой комиссией: P₁ = P₀, нет достоверного
 *     результата, никто из оставшихся не угадал направление, все спасовали.
 *     Остался один игрок → он забирает все банки без проверки прогноза (комиссия удерживается).
 *  5. В каждом банке выигрывают только претенденты с верным направлением:
 *     вес = вклад_в_банк / (1 + ошибка/шаг). Банк делится пропорционально весам;
 *     дробные остатки (0,01 кр.) — методом наибольших остатков, при равенстве — по порядку хода.
 *     Банк без претендентов с верным направлением возвращается внёсшим его, комиссия с него не берётся.
 *  6. Инвариант: списано у игроков = выплачено + возвращено + удержано банком.
 */
import type { Direction, Minor } from '@mt/protocol';
import { ROUNDS, type TableRules } from './rules.ts';

export interface SettlementSeat {
  playerId: string;
  /** позиция в порядке хода — для детерминированного распределения остатков */
  order: number;
  ante: Minor;
  /** чистые ставки по кругам (длина ROUNDS) */
  roundBets: Minor[];
  folded: boolean;
  direction: Direction | null;
  pointsTicks: number | null;
}

export type Outcome =
  | { kind: 'market'; deltaTicks: number }
  | { kind: 'walkover' }
  | { kind: 'invalid'; reason: string }
  | { kind: 'abort'; reason: string };

export interface PotShare {
  playerId: string;
  /** вес в виде дроби вклад/(1+ошибка_в_шагах), строкой для отображения */
  weight: string;
  amount: Minor;
}

export interface Pot {
  index: number;
  amount: Minor;
  /** вклад каждого игрока в этот банк */
  slices: Map<string, Minor>;
  eligible: string[];
  commission: Minor;
  refunded: boolean;
  shares: PotShare[];
}

export interface PlayerSettlement {
  playerId: string;
  contributed: Minor;
  payout: Minor;
  refund: Minor;
  errorTicks: number | null;
  correctDirection: boolean;
}

export interface RoundCommission {
  round: number;
  newContributions: Minor;
  commission: Minor;
  cap: Minor | null;
}

export interface SettlementResult {
  status: 'completed' | 'cancelled';
  kind: 'market' | 'walkover' | 'cancel';
  reason: string | null;
  deltaTicks: number | null;
  players: PlayerSettlement[];
  pots: Pot[];
  commissionByRound: RoundCommission[];
  /** номинальная комиссия по кругам */
  commissionTotal: Minor;
  /** фактически удержано банком (за вычетом комиссии возвращённых банков) */
  bankWithheld: Minor;
  totals: { debited: Minor; paid: Minor; refunded: Minor; bank: Minor; balanced: boolean };
}

export function contributionOf(s: SettlementSeat): Minor {
  return s.ante + s.roundBets.reduce((a, b) => a + b, 0);
}

export function computeCommission(seats: SettlementSeat[], rules: TableRules): RoundCommission[] {
  const out: RoundCommission[] = [];
  for (let r = 0; r < ROUNDS; r++) {
    let fresh = 0;
    for (const s of seats) fresh += (r === 0 ? s.ante : 0) + (s.roundBets[r] ?? 0);
    const cap = rules.commissionCapPerRound[r] ?? null;
    let commission = Math.floor((fresh * rules.commissionBps) / 10_000);
    if (cap !== null) commission = Math.min(commission, cap);
    out.push({ round: r + 1, newContributions: fresh, commission, cap });
  }
  return out;
}

export function buildPots(seats: SettlementSeat[]): Pot[] {
  const contrib = new Map(seats.map((s) => [s.playerId, contributionOf(s)]));
  const levels = [...new Set([...contrib.values()].filter((v) => v > 0))].sort((a, b) => a - b);
  const pots: Pot[] = [];
  let prev = 0;
  for (const level of levels) {
    const slices = new Map<string, Minor>();
    let amount = 0;
    for (const s of seats) {
      const c = contrib.get(s.playerId)!;
      const slice = Math.min(c, level) - Math.min(c, prev);
      if (slice > 0) {
        slices.set(s.playerId, slice);
        amount += slice;
      }
    }
    const eligible = seats.filter((s) => !s.folded && contrib.get(s.playerId)! >= level).map((s) => s.playerId);
    const last = pots[pots.length - 1];
    if (last && (eligible.length === 0 || sameSet(eligible, last.eligible))) {
      last.amount += amount;
      for (const [p, v] of slices) last.slices.set(p, (last.slices.get(p) ?? 0) + v);
    } else {
      pots.push({ index: pots.length, amount, slices, eligible, commission: 0, refunded: false, shares: [] });
    }
    prev = level;
  }
  return pots;
}

function sameSet(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((x) => b.includes(x));
}

/** Распределение целого total пропорционально целым весам (BigInt), метод наибольших остатков. */
export function allocateProportional(total: Minor, weights: bigint[], tieOrder: number[]): Minor[] {
  const sum = weights.reduce((a, b) => a + b, 0n);
  if (sum === 0n) return weights.map(() => 0);
  const T = BigInt(total);
  const base = weights.map((w) => (T * w) / sum);
  const rem = weights.map((w) => (T * w) % sum);
  let left = T - base.reduce((a, b) => a + b, 0n);
  const idx = weights.map((_, i) => i).sort((i, j) => {
    const d = rem[j]! - rem[i]!;
    if (d !== 0n) return d > 0n ? 1 : -1;
    return tieOrder[i]! - tieOrder[j]!;
  });
  for (const i of idx) {
    if (left <= 0n) break;
    base[i] = base[i]! + 1n;
    left -= 1n;
  }
  return base.map(Number);
}

export function settle(seats: SettlementSeat[], outcome: Outcome, rules: TableRules): SettlementResult {
  const commissionByRound = computeCommission(seats, rules);
  const commissionTotal = commissionByRound.reduce((a, r) => a + r.commission, 0);
  const pots = buildPots(seats);
  const orderOf = new Map(seats.map((s) => [s.playerId, s.order]));
  const players = new Map<string, PlayerSettlement>(
    seats.map((s) => [
      s.playerId,
      { playerId: s.playerId, contributed: contributionOf(s), payout: 0, refund: 0, errorTicks: null, correctDirection: false },
    ]),
  );
  const debited = [...players.values()].reduce((a, p) => a + p.contributed, 0);
  const remaining = seats.filter((s) => !s.folded);

  const cancel = (reason: string): SettlementResult => {
    for (const p of players.values()) p.refund = p.contributed;
    for (const pot of pots) {
      pot.refunded = true;
      pot.commission = 0;
    }
    return finish('cancelled', 'cancel', reason, 0);
  };

  const finish = (
    status: SettlementResult['status'],
    kind: SettlementResult['kind'],
    reason: string | null,
    bankWithheld: Minor,
    deltaTicks: number | null = null,
  ): SettlementResult => {
    const list = [...players.values()];
    const paid = list.reduce((a, p) => a + p.payout, 0);
    const refunded = list.reduce((a, p) => a + p.refund, 0);
    return {
      status,
      kind,
      reason,
      deltaTicks,
      players: list,
      pots,
      commissionByRound: status === 'cancelled' ? commissionByRound.map((r) => ({ ...r, commission: 0 })) : commissionByRound,
      commissionTotal: status === 'cancelled' ? 0 : commissionTotal,
      bankWithheld,
      totals: { debited, paid, refunded, bank: bankWithheld, balanced: debited === paid + refunded + bankWithheld },
    };
  };

  if (outcome.kind === 'abort') return cancel(outcome.reason);
  if (debited === 0) return cancel('нет взносов');
  if (remaining.length === 0) return cancel('все участники выбыли');

  // распределение комиссии по банкам
  const potComm = allocateProportional(
    commissionTotal,
    pots.map((p) => BigInt(p.amount)),
    pots.map((p) => p.index),
  );
  pots.forEach((p, i) => (p.commission = potComm[i]!));

  if (remaining.length === 1) {
    const winner = remaining[0]!.playerId;
    for (const pot of pots) {
      const net = pot.amount - pot.commission;
      pot.shares = [{ playerId: winner, weight: '—', amount: net }];
      players.get(winner)!.payout += net;
    }
    return finish('completed', 'walkover', 'все остальные участники спасовали', commissionTotal);
  }

  if (outcome.kind === 'walkover') return cancel('внутренняя ошибка: walkover при нескольких участниках');
  if (outcome.kind === 'invalid') return cancel(outcome.reason);

  const delta = outcome.deltaTicks;
  if (delta === 0) return cancel('P₁ = P₀: ни «выше», ни «ниже» не наступило');
  const dir: Direction = delta > 0 ? 'up' : 'down';
  const absDelta = Math.abs(delta);

  const correct = new Set<string>();
  for (const s of remaining) {
    if (s.direction === dir && s.pointsTicks !== null && s.pointsTicks > 0) {
      correct.add(s.playerId);
      const pl = players.get(s.playerId)!;
      pl.correctDirection = true;
      pl.errorTicks = Math.abs(s.pointsTicks - absDelta);
    }
  }
  if (correct.size === 0) {
    const r = cancel('никто из оставшихся игроков не угадал направление');
    r.deltaTicks = delta;
    return r;
  }

  let withheld = 0;
  for (const pot of pots) {
    const claimants = pot.eligible.filter((p) => correct.has(p));
    if (claimants.length === 0) {
      pot.refunded = true;
      pot.commission = 0; // комиссия с возвращённого банка не удерживается
      for (const [p, v] of pot.slices) players.get(p)!.refund += v;
      continue;
    }
    withheld += pot.commission;
    const net = pot.amount - pot.commission;
    const denoms = claimants.map((p) => BigInt(1 + players.get(p)!.errorTicks!));
    const common = denoms.reduce((a, b) => a * b, 1n);
    const weights = claimants.map((p, i) => BigInt(pot.slices.get(p) ?? 0) * (common / denoms[i]!));
    const amounts = allocateProportional(net, weights, claimants.map((p) => orderOf.get(p)!));
    pot.shares = claimants.map((p, i) => {
      players.get(p)!.payout += amounts[i]!;
      const slice = pot.slices.get(p) ?? 0;
      const w = slice / 100 / Number(denoms[i]!);
      return { playerId: p, weight: w.toFixed(4), amount: amounts[i]! };
    });
  }
  return finish('completed', 'market', null, withheld, delta);
}
